import React, {
    useContext,
    useEffect,
    useState,
    useCallback,
    createContext,
} from "react";
import { useAuth } from "hooks/Auth";
import { apiGet } from "helpers/Request";
import { transform as authorTransformer } from "schemas/Author";
import { getPaginationMeta } from "schemas/Util";
import MainBlock from "components/DesignSystem/LayoutBlocks/MainBlock";
import { useLocation, useHistory } from "react-router-dom";
import Pagination from "components/General/Pagination";
import { AppContext } from "context/App";
import AuthorListTable from "./AuthorListTable";
import AuthorListToolbar from "./AuthorListToolbar";
import ButtonGroup from "components/Buttons/ButtonGroup";
import { mergeRequestUrl } from "Utils/Urls";

export const AuthorListContext = createContext();

export default function AuthorScreen(props) {
    const auth = useAuth();
    const location = useLocation();
    const history = useHistory();
    const { setCurrentNav } = useContext(AppContext);

    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [paginationMeta, setPaginationMeta] = useState({});
    const [queryParams, setQueryParams] = useState(
        new URLSearchParams(location.search)
    );

    const setSelected = (e, id) => {
        if (e.target.checked) {
            setSelectedItems([...selectedItems, id]);
        } else {
            setSelectedItems(selectedItems.filter((item) => item !== id));
        }
    };

    const onResetFilters = () => {
        history.replace(location.pathname + "?");
    };

    const load = useCallback(() => {
        setLoading(true);
        setItems([]);
        setSelectedItems([]);
        const defaultParams = {
            per_page: 40,
            dir: "asc",
            order: "name",
        };
        const definedParams = Object.fromEntries(
            new URLSearchParams(location.search)
        );

        apiGet(
            "/v4/author",
            auth.publication,
            Object.assign({}, defaultParams, definedParams)
        ).then((res) => {
            setItems(authorTransformer(res));
            setPaginationMeta(getPaginationMeta(res));
            setLoading(false);
        });
    }, [location, auth.publication]);

    const updateParams = (key, val = null) => {
        history.replace(mergeRequestUrl(location, key, val));
    };

    useEffect(() => {
        load();
        setQueryParams(new URLSearchParams(location.search));
    }, [location, load]);

    useEffect(() => {
        setCurrentNav("author");
    }, [setCurrentNav]);

    return (
        <MainBlock>
            <AuthorListContext.Provider
                value={{ items, selectedItems, loading, load }}
            >
                <AuthorListToolbar queryParams={queryParams} />
                <div className="my-4 flex flex-wrap gap-2">
                    <div>
                        <ButtonGroup
                            variant="xs"
                            buttons={[
                                <button
                                    type="button"
                                    key="set-active"
                                    onClick={() => {
                                        updateParams("status", "active");
                                    }}
                                >
                                    Active
                                </button>,
                                <button
                                    type="button"
                                    key="set-active"
                                    onClick={() => {
                                        updateParams("status", "inactive");
                                    }}
                                >
                                    Inactive
                                </button>,
                            ]}
                        />
                    </div>
                    <div className="">
                        <ButtonGroup
                            variant="xs"
                            buttons={[
                                <button
                                    type="button"
                                    key="reset-filters"
                                    onClick={onResetFilters}
                                >
                                    Reset
                                </button>,
                            ]}
                        />
                    </div>
                    <div className="md:grid md:flex-grow md:justify-items-end">
                        <Pagination
                            variant="xs"
                            maxPages={3}
                            basePath="author"
                            {...paginationMeta}
                        />
                    </div>
                </div>
                <AuthorListTable
                    addSelected={setSelected}
                    setAllSelected={setSelectedItems}
                    queryParams={queryParams}
                />
            </AuthorListContext.Provider>

            <div className="my-4">
                <Pagination basePath="author" {...paginationMeta} />
            </div>
        </MainBlock>
    );
}
